import utf8 from 'utf8'
import base64 from 'base-64'
import Decimal from 'decimal.js'
import cloneDeep from 'lodash/cloneDeep'
import {format, startOfDay, endOfDay} from 'date-fns'
import {imageResize, saveBarcodeImage} from './ImageProcessor'
import guessCarrier from './guessCarrier'
import { thirdPartModuleConfig } from '../../config/moduleConfig'
import xlsx from 'xlsx'

const usStreetTypes = require('../assets/us-street-types.json')
const allStates = require('../assets/us-states.json')

function parseAddress (allCities, address) {
  const usStreetDirectional = {
    north: 'N',
    northeast: 'NE',
    east: 'E',
    southeast: 'SE',
    south: 'S',
    southwest: 'SW',
    west: 'W',
    northwest: 'NW'
  }

  const usLine2Prefixes = {
    'APARTMENT': 'APT',
    'APT': 'APT',
    'BASEMENT': 'BSMT',
    'BSMT': 'BSMT',
    'BLDG': 'BLDG',
    'BUILDING': 'BLDG',
    'DEPARTMENT': 'DEPT',
    'DEPT': 'DEPT',
    'FL': 'FL',
    'FLOOR': 'FL',
    'FRNT': 'FRNT',
    'FRONT': 'FRNT',
    'HANGAR': 'HNGR',
    'HNGR': 'HNGR',
    'LBBY': 'LBBY',
    'LOBBY': 'LBBY',
    'LOT': 'LOT',
    'LOWER': 'LOWR',
    'LOWR': 'LOWER',
    'OFC': 'OFC',
    'OFFICE': 'OFC',
    'PENTHOUSE': 'PH',
    'PH': 'PH',
    'PIER': 'PIER',
    'REAR': 'REAR',
    'RM': 'RM',
    'ROOM': 'RM',
    'SIDE': 'SIDE',
    'SLIP': 'SLIP',
    'SPACE': 'SPC',
    'SPC': 'SPC',
    'STE': 'STE',
    'STOP': 'STOP',
    'SUITE': 'STE',
    'TRAILER': 'TRLR',
    'TRLR': 'TRLR',
    'UNIT': 'UNIT',
    'UPPER': 'UPPR',
    'UPPR': 'UPPR',
    '#': '#'
  }

  // Validate a non-empty string was passed
  if (!address) {
    throw Error('Argument must be a non-empty string.')
  }
  let result = {}

  // Deal with any repeated spaces
  address = address.replace(/  +/g, ' ')
  // Assume comma, newline and tab is an intentional delimiter
  let addressParts = address.split(/,|\t|\n/)

  // Check if the last section contains country reference (Just supports US for now)
  let countrySection = addressParts[addressParts.length - 1].trim()
  if (countrySection === 'US' || countrySection === 'USA' || countrySection === 'United States' || countrySection === 'Canada') {
    addressParts.splice(-1, 1)
  }
    
  // Assume the last address section contains state, zip or both
  let stateString = addressParts[addressParts.length - 1].trim()

  // Parse and remove zip or zip plus 4 from end of string
  if (stateString.match(/\d{5}$/)) {
    result.zipCode = stateString.match(/\d{5}$/)[0]
    stateString = stateString.substring(0, stateString.length - 5).trim()
  } else if (stateString.match(/\d{5}-\d{4}$/)) {
    let zipString = stateString.match(/\d{5}-\d{4}$/)[0]
    result.zipCode = zipString.substring(0, 5)
    result.zipCodePlusFour = zipString
    stateString = stateString.substring(0, stateString.length - 10).trim()
  } else if (stateString.match(/[A-Z]\d[A-Z] ?\d[A-Z]\d/)) {
    result.zipCode = stateString.match(/[A-Z]\d[A-Z] ?\d[A-Z]\d/)[0]
    stateString = stateString.substring(0, stateString.length - result.zipCode.length).trim()
  }
  // Parse and remove state
  if (stateString.length > 0) { // Check if anything is left of last section
    addressParts[addressParts.length - 1] = stateString
  } else {
    addressParts.splice(-1, 1)
    stateString = addressParts[addressParts.length - 1].trim()
  }
  // First check for just an Abbreviation
  if (stateString.length === 2 && getKeyByValue(allStates, stateString.toUpperCase())) {
    result.state = stateString.toUpperCase()
    result.stateName = toTitleCase(getKeyByValue(allStates, stateString.toUpperCase()))
    stateString = stateString.substring(0, stateString.length - 2)
  } else {
    // Next check if the state string ends in state name or abbeviation
    // (state abbreviation must be preceded by a space to ensure accuracy)
    for (let key in allStates) {
      let re = new RegExp(' ' + allStates[key] + '$|' + key + '$', 'i')
      if (stateString.match(re)) {
        stateString = stateString.replace(re, '')
        result.state = allStates[key]
        result.stateName = toTitleCase(key)
        break
      }
    }
  }
  if (!result.state || result.state.length !== 2) {
    throw Error('Can not parse address. State not found.')
  }

  // Parse and remove city/place name
  let placeString = ''
  if (stateString.length > 0) { // Check if anything is left of last section
    addressParts[addressParts.length - 1] = stateString
    placeString = addressParts[addressParts.length - 1]
  } else {
    addressParts.splice(-1, 1)
    placeString = addressParts[addressParts.length - 1].trim()
  }
  result.city = ''
  allCities[result.state].some(function (element) {
    let re = new RegExp(element + '$', 'i')
    if (placeString.match(re)) {
      placeString = placeString.replace(re, '') // Carve off the place name
        
      result.city = element
      return element // Found a winner - stop looking for cities
    }
  })
  if (!result.city) {
    result.city = toTitleCase(placeString)
    placeString = ''
  }
    
  // Parse the street data
  let streetString = ''
  let usStreetDirectionalString = Object.keys(usStreetDirectional).map(x => usStreetDirectional[x]).join('|')
  let usLine2String = Object.keys(usLine2Prefixes).join('|')

  if (placeString.length > 0) { // Check if anything is left of last section
    addressParts[addressParts.length - 1] = placeString
  } else {
    addressParts.splice(-1, 1)
  }
    
  if (addressParts.length > 2) {
    throw Error('Can not parse address. More than two address lines.')
  } else if (addressParts.length === 2) {
    // check if the secondary data is first
    let re = new RegExp('^(' + usLine2String + ')\\b', 'i')
    if (addressParts[0].match(re)) {
      let tmpString = addressParts[1]
      addressParts[1] = addressParts[0]
      addressParts[0] = tmpString
    }
    // Assume street line is first
    result.address2 = addressParts[1].trim()
    addressParts.splice(-1, 1)
  }
  if (addressParts.length === 1) {
    streetString = addressParts[0].trim()
    // If no address line 2 exists check to see if it is incorrectly placed at the front of line 1
    if (typeof result.address2 === 'undefined') {
      let re = new RegExp('^(' + usLine2String + ')\\s\\S+', 'i')
      if (streetString.match(re)) {
        result.address2 = streetString.match(re)[0]
        streetString = streetString.replace(re, '').trim() // Carve off the line 2 data
      }
    }
    // Assume street address comes first and the rest is secondary address
    let reStreet = new RegExp('\\.\\*\\b(?:' + 
        Object.keys(usStreetTypes).join('|') + ')\\b\\.?' + 
        '( +(?:' + usStreetDirectionalString + ')\\b)?', 'i')
    let rePO = new RegExp('(P\\.?O\\.?|POST\\s+OFFICE)\\s+(BOX|DRAWER)\\s\\w+', 'i')
    let reAveLetter = new RegExp('\\.\\*\\b(ave.?|avenue)\\.\\*\\b[a-zA-Z]\\b', 'i')
    let reNoSuffix = new RegExp('\\b\\d+\\s[a-zA-Z0-9_ ]+\\b', 'i')
    if (streetString.match(reAveLetter)) {
      result.address1 = streetString.match(reAveLetter)[0]
      streetString = streetString.replace(reAveLetter, '').trim() // Carve off the first address line
      if (streetString && streetString.length > 0) {
        // Check if line2 data was already parsed
        if (result.hasOwnProperty('address2') && result.address2.length > 0) {
          throw Error('Can not parse address. Too many address lines. Input string: ' + address)
        } else {
          result.address2 = streetString
        }
      }
        
      let streetParts = result.address1.split(' ')
    
      // Assume type is last and number is first   
      result.streetNumber = streetParts[0] // Assume number is first element

      // Normalize to Ave
      streetParts[streetParts.length - 2] = streetParts[streetParts.length - 2].replace(/^(ave.?|avenue)$/i, 'Ave')

      // result.streetSuffix = toTitleCase(usStreetTypes[streetParts[streetParts.length-1].toLowerCase()]);
      result.streetName = streetParts[1] // Assume street name is everything in the middle
      for (let i = 2; i <= streetParts.length - 1; i++) {
        result.streetName = result.streetName + ' ' + streetParts[i]
      }
      result.streetName = toTitleCase(result.streetName)
      result.address1 = [result.streetNumber, result.streetName].join(' ')
    } else if (streetString.match(reStreet)) {
      result.address1 = streetString.match(reStreet)[0]
      streetString = streetString.replace(reStreet, '').trim() // Carve off the first address line
      if (streetString && streetString.length > 0) {
        // Check if line2 data was already parsed
        if (result.hasOwnProperty('address2') && result.address2.length > 0) {
          throw Error('Can not parse address. Too many address lines. Input string: ' + address)
        } else {
          result.address2 = streetString
        }
      }
      let streetParts = result.address1.split(' ')
    
      // Check if directional is last element
      let re = new RegExp('\\.\\*\\b(?:' + usStreetDirectionalString + ')$', 'i')
      if (result.address1.match(re)) {
        result.streetDirection = streetParts.pop().toUpperCase()
      }
        
      // Assume type is last and number is first   
      result.streetNumber = streetParts[0] // Assume number is first element
        
      // If there are only 2 street parts (number and name) then its likely missing a "real" suffix and the street name just happened to match a suffix
      if (streetParts.length > 2) {
        // Remove '.' if it follows streetSuffix
        streetParts[streetParts.length - 1] = streetParts[streetParts.length - 1].replace(/\.$/, '')
        result.streetSuffix = toTitleCase(usStreetTypes[streetParts[streetParts.length - 1].toLowerCase()])
      }
        
      result.streetName = streetParts[1] // Assume street name is everything in the middle
      for (let i = 2; i < streetParts.length - 1; i++) {
        result.streetName = result.streetName + ' ' + streetParts[i]
      }
      result.streetName = toTitleCase(result.streetName)
      result.address1 = [result.streetNumber, result.streetName].join(' ')
        
      if (result.hasOwnProperty('streetSuffix')) {
        result.address1 = result.address1 + ' ' + result.streetSuffix
      }
      if (result.streetDirection) {
        result.address1 = result.address1 + ' ' + result.streetDirection
      }
    } else if (streetString.match(rePO)) {
      result.address1 = streetString.match(rePO)[0]
      streetString = streetString.replace(rePO, '').trim() // Carve off the first address line
    } else if (streetString.match(reNoSuffix)) {
      // Check for a line2 prefix followed by a single word. If found peel that off as address2
      let reLine2 = new RegExp('\\s(' + usLine2String + ')\\.?\\s[a-zA-Z0-9_\\-]+$', 'i')
      if (streetString.match(reLine2)) {
        result.address2 = streetString.match(reLine2)[0].trim()
        streetString = streetString.replace(reLine2, '').trim() // Carve off the first address line
      }
        
      result.address1 = streetString.match(reNoSuffix)[0]
      streetString = streetString.replace(reNoSuffix, '').trim() // Carve off the first address line
      let streetParts = result.address1.split(' ')
    
      // Assume type is last and number is first   
      result.streetNumber = streetParts[0] // Assume number is first element
      streetParts.shift() // Remove the first element
      result.streetName = streetParts.join(' ') // Assume street name is everything else
    } else {
      throw Error('Can not parse address. Invalid street address data. Input string: ' + address)
    }
  } else {
    throw Error('Can not parse address. Invalid street address data. Input string: ' + address)
  }
    
  let addressString = result.address1
  if (result.hasOwnProperty('address2')) {
    addressString += ', ' + result.address2
  }
  if (addressString && result.hasOwnProperty('city') && result.hasOwnProperty('state') && result.hasOwnProperty('zipCode')) {
    let idString = addressString + ', ' + result.city + ', ' + result.state + ' ' + result.zipCode
    result['formattedAddress'] = idString
    result['id'] = encodeURI(idString.replace(/ /g, '-').replace(/\\#/g, '-').replace(/\//g, '-').replace(/\./g, '-'))
  }

  return result
}

function getKeyByValue (object, value) {
  return Object.keys(object).find(key => object[key] === value)
}

function toTitleCase (str) {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  })
}

const Logger = {
  store: null,
  initialize: (vueStore) => {
    Logger.store = vueStore
  },
  log: (action, payload) => {
    let newPayload = payload
    Logger.store.dispatch('logEvent', [action, newPayload])
  },
  critical: (action, payload) => {
    let newPayload = payload
    Logger.store.dispatch('logCriticalEvent', [action, newPayload])
  },
  changeLog: (payload) => {
    Logger.store.dispatch('addChangeLog', payload)
  }
}

class ApiError extends Error {
  constructor (errCode, httpCode, message) {
    super(typeof message === 'object' ? JSON.stringify(message) : message)
    this.errCode = errCode || 'general'
    this.httpCode = httpCode || 500
    if (typeof message === 'object') {
      this.msgObj = message
    }
  }
}

function axiosWrapper (promise) {
  return promise
    .catch((error) => {
      if (error.response) {
        console.log(error.response)
        let {status, data} = error.response
        throw new ApiError(data.error.code, status, data.error.message)
      } else if (error.request) {
        console.log(error.request)
        throw error
      } else {
        console.log('Error', error.message)
        throw error
      }
    })
}

function capitalize (str) {
  return str ? str.slice(0, 1).toUpperCase() + str.slice(1) : ''
}

function sleep (period) {
  return new Promise(resolve => { setTimeout(resolve, period) })
}

const checkRules = {
  methods: {
    fieldIsRequired (fieldName) {
      return v => {
        const errorMsg = this.$t('fieldIsRequired', { fieldName: capitalize(fieldName) || 'This field' })
        if (Array.isArray(v)) {
          return v.length > 0 || errorMsg
        }
        if (v !== undefined && v !== null && typeof v === 'string') v = v.trim()
        return (!!v && JSON.stringify(v) !== '{}') || v === 0 || errorMsg
      }
    },
    fieldIsInteger (fieldName) {
      return v => v % 1 === 0 || v === null || this.$t('fieldIsInteger', { fieldName: capitalize(fieldName) || 'This field' })
    },
    fieldIsNumber (fieldName) {
      return v => v >= 0 || v < 0 || this.$t('fieldIsNumber', { fieldName: capitalize(fieldName) || 'This field' })
    },
    fieldIsOverZero (fieldName) {
      return v => v > 0 || v === null || this.$t('fieldIsOverZero', { fieldName: capitalize(fieldName) || 'This field' })
    },
    fieldIsNoLessThanZero (fieldName) {
      return v => v >= 0 || v === null || this.$t('fieldIsNoLessThanZero', { fieldName: capitalize(fieldName) || 'This field' })
    },
    fieldIsZip () {
      return v => !v ||
        /^\d{5}$/.test(v) || 
        (/^\d{5}$/.test(v.split('-')[0]) && 
          (/^\d{4}$/.test(v.split('-')[1]) || 
            /^\d{5}$/.test(v.split('-')[1]))) ? true : this.$t('fieldIsZip')
    },
    fieldIsPhone () {
      return v => /^(\d{10}$)|(^\d{3}-\d{3}-\d{4}$)/.test(v) ? true : this.$t('fieldIsPhone')
    },
    fieldIsEmail () {
      return v => /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || this.$t('fieldIsEmail')
    },
    fieldCharLimit (fieldName, size = 0) {
      return v => !v || v.length <= size || this.$t('fieldCharLimit', { fieldName: capitalize(fieldName) || 'This field', size })
    },
    fieldIsPkgWeight (isOz) {
      return (v) => isOz ? (v >= 1 || this.$t('fieldIsPkgWeight')) : (v >= 0.0625 || this.$t('fieldIsPkgWeight'))
    },
    fieldIsCreditCardNumber () {
      return v => (/^\d{13,16}$/.test(v && v.split(' ').join(''))) || this.$t('creditCardNumberIsInvalid')
    }
  }
}

const labelServiceComponentTools = {
  data () {
    return {
      _filteredFromStateShippingServices: []
    }
  },
  methods: {
    filterShippingServiceByFromState (shippingServices, fromState) {
      const isParticularState = shippingServices.some(shippingService => 
        shippingService.supportParticularStates && shippingService.supportParticularStates.includes(fromState)
      )
      this._filteredFromStateShippingServices = shippingServices.filter(shippingService => 
        (!isParticularState || shippingService.supportParticularStates?.includes(fromState)) && 
        (!fromState || (!(shippingService.allowedFromStates || []).length || 
        shippingService.allowedFromStates.includes(fromState)))
      )
      return this._filteredFromStateShippingServices
    },
    checkShipForm () {
      return (v) => this._filteredFromStateShippingServices.some(({value}) => value === v) || this.$t('shippingServiceIsRequired')
    }
  }
}

const warehouseTools = {
  methods: {
    guessCarrier
  }
}

function runRules (rules, val) {
  if (Array.isArray(rules)) {
    return !rules.some(func => { return func(val) !== true })
  } else if (typeof rules === 'function') {
    return rules(val) === true
  } else {
    return false
  }
}

const decimalTools = {
  methods: {
    toMoney (item, digits = 2) {
      return new Decimal(item).toDP(digits).toNumber()
    }
  }
}

const measurementTools = {
  methods: {
    cm_inch (cm) {
      return new Decimal(cm / 2.54).toDP(2).toNumber()
    },
    inch_cm (inch) {
      return new Decimal(inch * 2.54).toDP(2).toNumber()
    },
    kg_lbs (kg) {
      return new Decimal(kg * 2.205).toDP(2).toNumber()
    },
    lbs_kg (lbs) {
      return new Decimal(lbs / 2.205).toDP(2).toNumber()
    },
    convertPackagingSize ({height, length, width}, isMeasurementMetric, isRound = false, isModifyPkg = false) {
      if (!!isMeasurementMetric === !!this.$store.getters.isMeasurementMetric) {
        return {height, length, width}
      }
      let convertFunc = this[`${isMeasurementMetric ? 'cm' : 'inch'}_${this.$store.getters.isMeasurementMetric ? 'cm' : 'inch'}`]
      if (isModifyPkg && isMeasurementMetric) convertFunc = this.inch_cm
      if (isModifyPkg && !isMeasurementMetric) convertFunc = this.cm_inch
      return {
        height: isRound ? Math.round(convertFunc(height)) : convertFunc(height), 
        length: isRound ? Math.round(convertFunc(length)) : convertFunc(length), 
        width: isRound ? Math.round(convertFunc(width)) : convertFunc(width)
      }
    },
    convertWeight (weight, isMeasurementMetric, isModifyPkg = false) {
      if (!!isMeasurementMetric === !!this.$store.getters.isMeasurementMetric) {
        return weight
      }
      let convertFunc = this[`${isMeasurementMetric ? 'kg' : 'lbs'}_${this.$store.getters.isMeasurementMetric ? 'kg' : 'lbs'}`]
      if (isModifyPkg && isMeasurementMetric) convertFunc = this.lbs_kg
      if (isModifyPkg && !isMeasurementMetric) convertFunc = this.kg_lbs
      return convertFunc(weight)
    }
  }
}

const stringTools = {
  methods: {
    capitalize (s) {
      if (typeof s !== 'string') return ''
      return s.charAt(0).toUpperCase() + s.slice(1)
    },
    trimNonwordChar (barcode) {
      return barcode.replace(/[^\w-]/g, '')
    }
  }
}

const calculateQtyMethod = {
  methods: {
    calAndAddQtyfromHistoryPkgs (historyPackages, isFedexMwt = false) {
      let packages = historyPackages.map(pkg => pkg.weightLb ? pkg : {...pkg, weightLb: this.convertWeight(pkg.weight), ...this.convertPackagingSize({length: pkg.length, width: pkg.width, height: pkg.height})})
      if (isFedexMwt) return packages
      const ObjforMatchPackage = {}
      const mapForCalculateQty = new Map()
      packages.forEach(packageItem => {
        const {weightLb, length, width, height, isSureValue, memo, qty = 1} = packageItem
        const coreValueString = '' + weightLb + length + width + height + isSureValue + memo
        ObjforMatchPackage[coreValueString] = packageItem
        mapForCalculateQty.has(coreValueString) ? mapForCalculateQty.set(coreValueString, mapForCalculateQty.get(coreValueString) + 1) : mapForCalculateQty.set(coreValueString, qty || 1)
      })
      return Object.keys(ObjforMatchPackage).reduce((acc, cur, index) => {
        return [...acc, {...ObjforMatchPackage[cur], qty: mapForCalculateQty.get(cur)}]
      }, [])
    }
  }
}

function copyToClipboard (text) {
  var textArea = document.createElement('textarea')
  textArea.style.position = 'fixed'
  textArea.style.top = 0
  textArea.style.left = 0
  textArea.style.width = '2em'
  textArea.style.height = '2em'
  textArea.style.padding = 0
  textArea.style.border = 'none'
  textArea.style.outline = 'none'
  textArea.style.boxShadow = 'none'
  textArea.style.background = 'transparent'
  textArea.value = text
  document.body.appendChild(textArea)
  textArea.select()
  try {
    var successful = document.execCommand('copy')
    var msg = successful ? 'successful' : 'unsuccessful'
    console.log('Copying text command was ' + msg)
  } catch (err) {
    console.log('Oops, unable to copy')
  }
  document.body.removeChild(textArea)
}
function getApprovalTypeString (approvalType) {
  let status = ''
  switch (approvalType) {
    case 3:
      status = 'accepted'
      break
    case 2:
      status = 'limited'
      break
    case 1:
      status = 'blocked'
      break
    case 0:
      status = 'rejected'
      break
    case -1:
      status = 'unsubscribed'
      break
    default:
      break
  }
  return status
}

function getIsoTime () {
  return new Date().toISOString()
}

function getTime () {
  return new Date().getTime()
}

function getRandomIdByTime (withDigits = 0) {
  const timeString = Math.floor(getTime()).toString()
  let lastString = ''
  for (let i = 0; i < withDigits; i++) {
    lastString += Math.floor(Math.random() * 10).toString()
  }
  return timeString + lastString
}

function uniqueIdGenerator (qty) {
  // add 3 different digit based on current timestamp
  const curTime = Date.now()
  return new Array(qty).fill(1).map((item, index) => {
    let addOn = ''
    if (index < 10) {
      addOn = '00'
    } else if (index < 100) {
      addOn = '0'
    }
    return `${curTime}${addOn}${index}`
  })
}

/** get a random OrgId default 3 digits */
function getRandOrgId (digits = 3) {
  const letterPool = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  let output = ''
  for (let i = 0; i < digits; i++) {
    let index = Math.floor(Math.random() * Math.floor(letterPool.length))
    output += letterPool[index]
  }
  return output
}

const timezoneString = `${new Date().getTimezoneOffset() >= 0 ? '-' : '+'}${String(Math.abs(new Date().getTimezoneOffset() / 60)).padStart(2, '0')}:00`

function convertStringToDate (val) {
  return val && !(val instanceof Date) ? new Date(val + 'T00:00:00.000' + timezoneString) : val
}

function toTimestampString (date) {
  let rtn = convertStringToDate(date)
  return rtn && format(rtn, 'MM/dd/yyyy HH:mm')
}

function toDateString (date) {
  if (!(date instanceof Date) && (date instanceof Object) && date._methodName === 'FieldValue.serverTimestamp') return 'server time'
  let rtn = convertStringToDate(date)
  return rtn && format(rtn, 'MM/dd/yyyy')
}

function toPickerDateString (date) {
  let rtn = convertStringToDate(date)
  return rtn && format(rtn, 'yyyy-MM-dd')
}

function toDateStart (text) {
  let rtn = convertStringToDate(text)
  return rtn && startOfDay(rtn)
}

function toDateEnd (text) {
  let rtn = convertStringToDate(text)
  return rtn && endOfDay(rtn)
}

function toLocalDateString (date) {
  date = toDateEnd(date)
  let tzoffset = date.getTimezoneOffset() * 60000 // offset in milliseconds
  let localISOTime = (new Date(date - tzoffset)).toISOString().slice(0, 19).replace('T', ' ')
  return localISOTime
}

function isDateEqual (date1, date2) {
  return new Date(date1).valueOf() === new Date(date2).valueOf()
}

// function dateToISOStringDeep (obj) {
//   if (!obj) return obj

//   Object.entries(obj).forEach(([key, val]) => {
//     if (!val) {
//       obj[key] = val
//     } else if (val.toISOString) {
//       obj[key] = {value: val.toISOString(), isISOStrng: true}
//     } else if (typeof val === 'object') {
//       dateToISOStringDeep(val)
//     }
//   })
//   return obj
// }

// function reverseISOStringBackDeep (obj) {
//   if (!obj) return obj

//   Object.entries(obj).forEach(([key, val]) => {
//     if (!val) {
//       obj[key] = val
//     } else if (val.isISOStrng) {
//       obj[key] = new Date(val.value)
//     } else if (typeof val === 'object') {
//       reverseISOStringBackDeep(val)
//     }
//   })
//   return obj
// }

const timeTools = {
  methods: {
    toTimestampString,
    toDateString,
    toPickerDateString,
    toDateStart,
    toDateEnd,
    toLocalDateString,
    isDateEqual,
    getIsoTime () {
      return new Date().toISOString()
    },
    getTime () {
      return new Date().getTime()
    },
    toDateMMDDHHmm (date) {
      return date && format(date, 'MM/dd HH:mm')
    },
    toDateYYYYMMDD (date) {
      return date && format(date, 'yyyy-MM-dd')
    },
    toDateYYYYMMDDHHmm (date) {
      return date && format(date, 'yyyy-MM-dd HH:mm')
    },
    toDateYYYYMMDDHHmmss (date) {
      return date && format(date, 'yyyy-MM-dd HH:mm:ss')
    },
    formatDate (date) {
      if (!date) return null
      const [year, month, day] = date.split('-')
      return `${month}/${day}/${year}`
    }
  }
}

function getCurDateKeyStr () {
  const currentTime = new Date()
  const curMonthKeyStr = `${currentTime.getFullYear()}-${(currentTime.getMonth() + 1).toString().padStart(2, '0')}`
  return `${curMonthKeyStr}-${currentTime.getDate().toString().padStart(2, '0')}`
}

function splitTrackingNum (text) {
  text = text.replace(/\n/g, ' ')
  text = text.replace(/,/g, ' ')
  text = text.replace(/\./g, ' ')
  text = text.replace(/;/g, ' ')
  text = text.replace(/；/g, ' ')
  text = text.replace(/，/g, ' ')
  text = text.replace(/。/g, ' ')
  text = text.replace(/"/g, ' ')
  text = text.replace(/'/g, ' ')
  text = text.replace(/“/g, ' ')
  text = text.replace(/”/g, ' ')
  text = text.replace(/‘/g, ' ')
  text = text.replace(/’/g, ' ')
  text = text.replace(/\s+/g, ' ')
  text = text.trim()

  let arr = text.split(' ')
  return arr.filter(item => item !== '').map(tracking => tracking.toUpperCase())
}

function splitKeyword (text) {
  text = text.replace(/\n/g, ' ')
  text = text.replace(/,/g, ' ')
  text = text.replace(/\./g, ' ')
  text = text.replace(/，/g, ' ')
  text = text.replace(/。/g, ' ')
  text = text.replace(/\s+/g, ' ')
  text = text.trim()

  let rtn = text.toLowerCase().split(' ').filter(item => item !== '' && item !== '-' && typeof item === 'string')
  return [...new Set(rtn)]
}

function formatKeywords (keywords) {
  if (!Array.isArray(keywords)) return Error('keywords must be an array')
  return [...new Set(keywords.map(keyword => keyword.toLowerCase()))]
}

function base64Encoding (text) {
  return base64.encode(utf8.encode(text))
}

// true for equal, false for not equal
function compareArrayInOrder (a, b) {
  return a.every((item, index) => {
    let bItem = b[index]
    return bItem && Object.keys(item).every(key => {
      let rtnValue = false
      if (item[key] instanceof Date) {
        rtnValue = (item[key].getTime() === bItem[key].getTime())
      } else if (typeof item[key] === 'string') {
        rtnValue = (item[key] === bItem[key])
      } else {
        // may need to enhance deep compare of other type in the future
        rtnValue = true
      }
      return rtnValue
    })
  })
}

// return true if two objects have same fields
/** 
 * @param {Boolean} options.isSubset 指定a为b的子集,只对比a中的存在的key
 * @param {string} options.exceptKey 指定key的值不进行对比
*/
function deepEqual (a, b, options = {}) {
  if ((!a && b) || (a && !b)) return false
  if ((!a || (Array.isArray(a) && !a.length)) && (!b || (Array.isArray(b) && !b.length))) return true

  let aKeys = Object.keys(a)
  let bKeys = Object.keys(b)

  if (aKeys.length !== bKeys.length && !options.isSubset) return false

  return !aKeys.some(aKey => {
    if (aKey === options.exceptKey) {
      return false
    }
    if (a[aKey] === null && b[aKey] === null) {
      return false
    }
    if (typeof a[aKey] === 'object') {
      return a[aKey] && b[aKey] ? !deepEqual(a[aKey], b[aKey]) : true
    } else {
      return a[aKey] !== b[aKey]
    }
  })
}

function getNullFields (payload) {
  let undefinedFields = []
  Object.keys(payload).forEach(key => {
    if (typeof payload[key] === 'object' && payload[key].constructor.name !== 'Date' && payload[key].constructor.name !== 't') {
      let childHasUndefined = getNullFields(payload[key])
      childHasUndefined.length > 0 && (undefinedFields = [...undefinedFields, ...childHasUndefined])
    } else if (payload[key] == null || typeof payload[key] === 'undefined') {
      undefinedFields.push(key)
    }
  })

  return undefinedFields
}

// Deep transfer timestamps in an object into date type
function convertTimestampToDateInObj (obj) {
  if (!obj) return obj
  for (var a in obj) {
    if (typeof (obj[a]) === 'object' && obj[a] !== null) {
      if (typeof obj[a].toDate === 'function') {
        obj[a] = obj[a].toDate()
      } else {
        convertTimestampToDateInObj(obj[a])
      }
    }
  }
  return obj
}

// get the difference in value from a comparing to b. If c is provided, the data will put into the return object in the end
// note: it is shallow, so may not work with field of Object type
function getDifferenceShallow (a, b, c) {
  let rtn = {}
  Object.keys(a).forEach(key => {
    if (a[key] !== b[key]) rtn[key] = a[key]
  })

  c && (rtn = {...rtn, ...c})

  return rtn
}

// add number with decimal
function addNumbers (...items) {
  return items.reduce((sum, item) => {
    return sum.plus(item)
  }, new Decimal(0)).toDP(2).toNumber()
}

function toMoney (item) {
  return new Decimal(item).toDP(2).toNumber()
}

function splitProductName (text = '') {
  let originText = text.toLowerCase()

  text = text.replace(/\n/g, ' ')
  text = text.replace(/,/g, ' ')
  text = text.replace(/\./g, ' ')
  text = text.replace(/，/g, ' ')
  text = text.replace(/。/g, ' ')
  text = text.replace(/\s+/g, ' ')
  text = text.trim()

  let arr = text.toLowerCase().split(' ').filter(item => item !== '' && item !== '-')
  let arr2 = []
  for (let i = 0; i < arr.length - 1; i++) {
    arr2.push(`${arr[i]} ${arr[i + 1]}`)
  }
  arr = [...arr, ...arr2, originText]
  return arr
}

function sortNoCase (fieldName) {
  if (fieldName) {
    return (a, b) => {
      if (a[fieldName].toString().toUpperCase() > b[fieldName].toString().toUpperCase()) {
        return 1
      } else if (a[fieldName].toString().toUpperCase() === b[fieldName].toString().toUpperCase()) {
        return 0
      } else {
        return -1
      }
    } 
  } else {
    return (a, b) => {
      if (a.toString().toUpperCase() > b.toString().toUpperCase()) {
        return 1
      } else if (a.toString().toUpperCase() === b.toString().toUpperCase()) {
        return 0
      } else {
        return -1
      }
    } 
  }
}

function formatPhoneNumber (phoneNumber) {
  if (!phoneNumber) return ''
  let phoneNumberString = phoneNumber.trim()
  var cleaned = ('' + phoneNumberString).replace(/\D/g, '')
  var match = cleaned.match(/^(.*|)?(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    var intlCode = (match[1] ? `+${match[1]}` : '')
    return [intlCode, ' (', match[2], ') ', match[3], '-', match[4]].join('')
  }
  return phoneNumberString
}

const mediaTools = {
  data () {
    return {
      audioCtx: null
    }
  },
  methods: {
    makeBeepSoundFunc (frequency = 500, volume = 1, duration = 250, soundType = 'square') {
      this.audioCtx = this.audioCtx || new (window.AudioContext || window.webkitAudioContext)()
      if (!this.audioCtx) {
        console.warn('This browser does not support audio context')
        return false
      }
      return () => {
        let oscillator = this.audioCtx.createOscillator()
        let gainNode = this.audioCtx.createGain()
      
        oscillator.connect(gainNode)
        gainNode.connect(this.audioCtx.destination)
      
        gainNode.gain.value = volume
        oscillator.frequency.value = frequency
        oscillator.type = soundType
      
        oscillator.start()
        oscillator.stop(this.audioCtx.currentTime + duration / 1000)
      }
    },
    hintSound (scheme = 1) {
      let frequency = 900 + 400 * (scheme - 1)
      return this.makeBeepSoundFunc(frequency, 1, 200)
    },
    upcSound (scheme = 1) {
      let frequency = 2000 + 400 * (scheme - 1)
      return this.makeBeepSoundFunc(frequency, 1, 200)
    },
    trackingSound (scheme = 1) {
      let frequency = 300 + 400 * (scheme - 1)
      return this.makeBeepSoundFunc(frequency, 1, 200)
    },
    successSound (scheme = 1) {
      let frequency = 1500 + 400 * (scheme - 1)
      return this.makeBeepSoundFunc(frequency, 1, 200)
    },
    warningSound (scheme = 1) {
      let frequency = 300 + 200 * (scheme - 1)
      let warning = this.makeBeepSoundFunc(frequency, 1, 200)
      return () => { 
        warning()
        setTimeout(warning, 350)
      }
    },
    unForecastTrackingSound (scheme = 1) {
      return () => {
        this.trackingSound(scheme)
        setTimeout(this.trackingSound(scheme), 350)
      }
    },
    unForecastUpcSound (scheme = 1) {
      return () => {
        this.upcSound(scheme)
        setTimeout(this.upcSound(scheme), 350)
      }
    },
    closeSound () {
      if (this.audioCtx) {
        return this.audioCtx.close()
          .then(() => (this.audioCtx = null))
      }
    }
  }
}

function getDefaultEmailTemplates () {
  return {
    approved: {
      template: `
Dear #{orgName}:

Congratulations. Your warehouse signup request to #{warehouse} has been approved.

Here is your shipping information:
Organization ID: #{orgId}
Shipment address: #{sampleAddress}

If you have any questions, feel free to contact us through warehouse/support or QQ.

Best regards,

#{warehouse}`,
      attachments: []
    },
    rejected: {
      template: `
Dear #{orgName}:

We are sorry to inform you that your warehouse signup request has been rejected. 

If you have further questions, feel free to contact our sales representative.

Best regards,

#{warehouse}`
    }
  }
}

/**
 * 
 * @param {string} tracking user input tracking string
 * @returns {string} string remove invisible char 
 */
function normalizeInputString (tracking) {
  return (tracking || '').replace(/[^\u0020-\u007E]/g, '').trim()
}

function isObject (data) {
  return typeof data !== 'object'
}

function gotoGoogleMap ({address1, address2, city, state, zipCode} = {}) {
  const locationString = `${address1}, ${address2 ? `${address2}, ` : ''}${city}, ${state}, ${zipCode}`.split(' ').join('+')
  window.open(`https://www.google.com/maps/place/${locationString}`, '_blank')
}

function downloadLabelFile (labelUrl, fileName) {
  return new Promise((resolve, reject) => {
    let request = new XMLHttpRequest()
    request.responseType = 'blob'
    request.onload = function (event) {
      // Only handle status code 200
      if (request.status === 200) {
        // The actual download
        var blob = new Blob([request.response])
        var link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = fileName

        document.body.appendChild(link)

        link.click()

        document.body.removeChild(link)
        
        return resolve()
      }
    }
    request.open('GET', labelUrl)
    request.send()
  })
}

function isEmptyObject (obj) {
  return !Object.keys(obj).length
}

function warehousesSitesPredicates (warehouseKey, roles = [], sites = [], field = '') {
  const predicates = []
  if (warehouseKey && !roles.includes('warehouseOwner') && !sites.includes('all') && field) {
    predicates.push({
      field,
      compare: 'in',
      value: sites.length ? sites : ['']
    })
  }
  return predicates
}

function validateUserModuleAuthority (myAuthorities, moduleKey, authorityRolePort) {
  if (authorityRolePort === 'admin') return true
  const authorityRoles = {
    '0': 'org',
    '1': 'user',
    '2': 'warehouse'
  }
  const systemRole = authorityRoles[authorityRolePort]
  const isOwner = myAuthorities.includes(`${systemRole}Owner`)
  if (isOwner) return true
  if (myAuthorities && myAuthorities.includes(systemRole + moduleKey)) return true
  return false
}

function loadDataRTAsync (successFunc, errorFunc, cb) {
  return new Promise((resolve, reject) => {
    let resolveFunc = resolve
    let rejectFunc = reject
    const successFuncResolve = (...args) => {
      successFunc(...args)
      if (resolveFunc) {
        resolveFunc()
        resolveFunc = null
      }
    }
    const errorFuncReject = (...args) => {
      errorFunc(...args)
      if (rejectFunc) {
        rejectFunc()
        rejectFunc = null
      }
    }
    cb && cb(successFuncResolve, errorFuncReject)
  })
}

function arrayToObj (arr = [], keyName, valueName) {
  const obj = {}
  if (!keyName || !valueName || !Array.isArray(arr)) return obj
  arr.forEach(item => {
    const objKey = item[keyName]
    if ([undefined, null].includes(objKey)) return
    obj[objKey] = item[valueName]
  })
  return obj
}

function getFieldListColumnPosition (xlsx, workBook, workSheetName, fieldList, rowIndex) {
  const headerMatchIndex = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']
  const operatingWorkSheet = workBook.Sheets[workSheetName]
  const alphabetIndexMap = new Map()
  const columnCount =
    xlsx.utils.decode_range(operatingWorkSheet['!ref']).e.c + 1
  for (let i = 0; i < columnCount; ++i) {
    fieldList.forEach(field => {
      if (field.toLowerCase().includes(operatingWorkSheet[`${xlsx.utils.encode_col(i)}${rowIndex}`].v.toLowerCase().trim())) {
        alphabetIndexMap.set(field, headerMatchIndex[i])
      }
    })
  }
  return alphabetIndexMap
}

function getBetweenDate (startDate, endDate) {
  if (!startDate || !endDate) return []
  const startTime = startDate.getTime()
  const endTime = endDate.getTime()
  const diffTime = endTime - startTime
  const dates = []
  for (let i = 0; i <= diffTime; i += 86400000) {
    const nextDate = new Date(startTime + i)
    dates.push(nextDate)
  }
  return [...new Set([...dates, endDate])]
}

// module管理Navbar显示的路由
function getConditionalRoutesWithDifferentEnv (routes = [], env) {
  const availableModules = thirdPartModuleConfig[env] || thirdPartModuleConfig['viteusa']
  return routes.filter(routeObj => {
    return !routeObj.module || routeObj.module.every(moduleName => availableModules.includes(moduleName))
  }).map(item => {
    if (item.subItems && item.subItems.length) {
      item.subItems = item.subItems.filter(subItem => !subItem.module || subItem.module.every(moduleName => availableModules.includes(moduleName)))
    }
    return item
  })
}
// }
// module管理routeDefination
function getDefinedConditionalRoutesWithEnv (routes = [], env) {
  const availableModules = thirdPartModuleConfig[env] || thirdPartModuleConfig['viteusa']
  return Object.fromEntries(Object.entries(routes).filter(([key, obj]) => {
    return obj && (!obj.meta || !obj.meta.module || obj.meta.module.every(moduleName => availableModules.includes(moduleName)))
  }))
}

function readXlsxSheetData (files, sheetsName) {
  if (files.length === 0) return
  let sheetName = sheetsName
  return new Promise((resolve, reject) => {
    let reader = new FileReader()
    reader.onload = event => {
      let fileResult = event.target.result
      let excel = xlsx.read(fileResult, { type: 'binary' })
      let name = sheetName || excel.SheetNames[0]
      let sheet = excel.Sheets[name]
      let rawData = xlsx.utils.sheet_to_json(sheet)
      return resolve(rawData || [])
    }
    reader.onerror = err => {
      return reject(err.message) 
    }
    reader.readAsBinaryString(files[0])
  })
}
function matchTargetNavigator (i18n) {
  const navigatorsVersionMap = {
    Chrome: 84
    // Edge: 79,
    // Firefox: 62
  }
  const userAgent = window.navigator.userAgent
  const curNavigator = Object.keys(navigatorsVersionMap)
    .find(navigator => userAgent.includes(navigator))
  if (!curNavigator) {
    alert(i18n.t('browserTarget'))
  } else {
    const reg = new RegExp(`${curNavigator}/(.\\d+)`)
    const [, version] = userAgent.match(reg)
    if (version < navigatorsVersionMap[curNavigator]) {
      alert(i18n.t('browserVersionTooLow'))
    }
  }
}

/** @param {fileName: string, data: (number | string)[]} obj */
const exportExcelFile = ({ fileName, data }) => {
  let str = ''
  data.forEach((item, i) => {
    item = item.map(row => {
      return String(row).replace(/,/g, ' ')
    })
    str += item.join(',') + (i === data.length - 1 ? '' : '\n')
  })
  const $a = document.createElement('a')
  $a.download = fileName
  $a.style.display = 'none'
  $a.href = 'data:text/csv;charset=utf-8,\ufeff' + str
  $a.click()
  $a.remove()
}

/** @param {data:any[]} array */
/** @param {fileName: string} obj */
function exportListToXlsx (data, fileName = 'data') {
  const book = xlsx.utils.book_new()
  const sheet = xlsx.utils.aoa_to_sheet(data)
  xlsx.utils.book_append_sheet(book, sheet, 'sheet1')
  xlsx.writeFile(book, `${fileName}.xlsx`)
}

/** @param {env: string, production|sandbox}  */
function importSquarePkg (env = 'sandbox') {
  return new Promise((resolve, reject) => {
    try {
      let predicate = `script[square-env=${env}]`
      let dom = document.querySelector(predicate)
      if (dom) return resolve()
      const scriptInfo = document.createElement('script')
      scriptInfo.type = 'text/javascript'
      scriptInfo.setAttribute('square-env', env)
      scriptInfo.src = env === 'sandbox' ? 'https://sandbox.web.squarecdn.com/v1/square.js' : 'https://web.squarecdn.com/v1/square.js'
      let scriptDom = document.head.appendChild(scriptInfo)
      if (scriptDom) {
        return resolve()
      }
    } catch (error) {
      throw reject(error)
    }
  })
}
/**
 * 
 * @param {string} text
 */
export function copyText (text = '') {
  const input = document.createElement('input')
  try {
    input.setAttribute('value', text)
    document.body.appendChild(input)
    input.select()
    document.execCommand('copy')
  } catch (error) {
    console.log(error.message)
    return error
  } finally {
    document.body.removeChild(input)
  }
}
/** @param {env: string, production|sandbox}  */
function importAuthPayPkg (env = 'sandbox') {
  return new Promise((resolve, reject) => {
    try {
      let predicate = `script[auth-env=${env}]`
      let dom = document.querySelector(predicate)
      if (dom) return resolve()

      const scriptInfo = document.createElement('script')
      scriptInfo.type = 'text/javascript'
      scriptInfo.setAttribute('auth-env', env)
      scriptInfo.src = env === 'production' ? 'https://js.authorize.net/v1/Accept.js' : 'https://jstest.authorize.net/v1/Accept.js'
      let scriptDom = document.head.appendChild(scriptInfo)
      if (scriptDom) {
        return resolve()
      }
    } catch (error) {
      throw reject(error)
    }
  })
}
/**
 * @param {string[]} trackings 
 * @param {dbAccessor} dbAccessor
 */
async function updateDownloadFieldByLabels (trackings, dbAccessor) {
  const getPredicates = (labels) => {
    if (labels.length > 10) throw Error('batch download is limited to 10 labels.')
    const predicates = [{
      field: 'trackingNumber',
      compare: 'in',
      value: labels
    }]
    return predicates
  }
  const labelsDocs = []
  const getLabelsDocs = async (labels, limit) => {
    console.log(labels.length)
    if (labels.length === 0) return []
    const curBatch = labels.splice(0, limit)
    const docs = await dbAccessor.queryWithPredicatesStore(getPredicates(curBatch), 'labels')
    await getLabelsDocs(labels, limit)
    labelsDocs.push(...docs.docs)
  }
  await getLabelsDocs(trackings, 10)
  if (labelsDocs.length === 0) return 'no docs'
  return dbAccessor.runInBatch(batch => {
    labelsDocs.forEach(doc => {
      batch.update(doc.ref, {isDownloaded: true})
    })
  })
}

export {
  copyToClipboard,
  getIsoTime,
  getTime,
  toTimestampString,
  toDateString,
  isDateEqual,
  toPickerDateString,
  toDateEnd,
  toDateStart,
  getApprovalTypeString,
  splitTrackingNum,
  base64Encoding,
  compareArrayInOrder,
  deepEqual,
  getNullFields,
  getDifferenceShallow,
  Logger,
  addNumbers,
  toMoney,
  convertTimestampToDateInObj,
  toLocalDateString,
  splitProductName,
  sortNoCase,
  formatPhoneNumber,
  checkRules,
  runRules,
  timeTools,
  decimalTools,
  mediaTools,
  sleep,
  getRandomIdByTime,
  capitalize,
  getDefaultEmailTemplates,
  cloneDeep,
  getRandOrgId,
  imageResize,
  warehouseTools,
  saveBarcodeImage,
  axiosWrapper,
  splitKeyword,
  normalizeInputString,
  stringTools,
  uniqueIdGenerator,
  measurementTools,
  isObject,
  gotoGoogleMap,
  getCurDateKeyStr,
  downloadLabelFile,
  calculateQtyMethod,
  isEmptyObject,
  warehousesSitesPredicates,
  validateUserModuleAuthority,
  loadDataRTAsync,
  labelServiceComponentTools,
  arrayToObj,
  getBetweenDate,
  readXlsxSheetData,
  matchTargetNavigator,
  exportExcelFile,
  getConditionalRoutesWithDifferentEnv,
  getDefinedConditionalRoutesWithEnv,
  parseAddress,
  formatKeywords,
  importSquarePkg,
  getFieldListColumnPosition,
  exportListToXlsx,
  importAuthPayPkg,
  updateDownloadFieldByLabels
}



// WEBPACK FOOTER //
// ./src/utils/tools.js